import { useResponsiveValue } from '@superdispatch/ui';
import { SidebarContainer } from '@superdispatch/ui-lab';
import { Navigate, RouteObject } from 'react-router-dom';
import { useFlag } from 'shared/settings/FeatureToggles';
import {
  SettingsContainerOutlet,
  SettingsSidebar,
} from './SettingsContainerOutlet';
import { PermissionView } from 'shared/modules/permission/core/PermissionView';
import { lazyImport } from 'shared/routing/LazyRoute';
import { useEdiSettingsAccess } from './data/useEdiSettingsAccess';
import { SettingsPageLayout } from './core/SettingsPageLayout';

const SettingsBillingPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsBillingPage',
);
const SettingsDriverAppPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsDriverAppPage',
);
const SettingsFactoringPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsFactoringPage',
);
const SettingsGeneralPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsGeneralPage',
);
const SettingsNotificationsPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsNotificationsPage',
);
const SettingsQuickbooksIntegrationPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsQuickbooksIntegrationPage',
);
const SettingsSubscriptionLegacyPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsSubscriptionLegacyPage',
);
const SettingsSubscriptionPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsSubscriptionPage',
);
const SettingsTerminalPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsTerminalPage',
);
const SettingsEdiPage = lazyImport(
  () => import('./SettingsPages'),
  'SettingsEdiPage',
);

export function SettingsQuickbooksIntegrationPageOutlet() {
  return (
    <PermissionView name="quickbooks" permission="can_view">
      <SettingsQuickbooksIntegrationPage />
    </PermissionView>
  );
}

export const settingsRoutes: RouteObject[] = [
  {
    path: 'settings/:page?',
    element: <SettingsContainerOutlet />,
    children: [
      {
        path: 'general',
        element: <SettingsGeneralPage />,
      },
      {
        path: 'terminals/:action?',
        element: <SettingsTerminalPage />,
      },
      {
        path: 'edi',
        element: <SettingsEdiRoute />,
      },
      {
        path: 'driver-app',
        element: <SettingsDriverAppPage />,
      },
      {
        path: 'billing',
        element: <SettingsBillingPage />,
      },
      {
        path: 'factoring',
        element: (
          <PermissionView name="factoring" permission="can_view">
            <SettingsFactoringPage />
          </PermissionView>
        ),
      },
      {
        path: 'quickbooks',
        element: <SettingsQuickbooksIntegrationPageOutlet />,
      },
      { path: 'subscription/:action?', element: <SettingsSubscriptionRoute /> },
      {
        path: 'notifications',
        element: <SettingsNotificationsPage />,
      },
      {
        path: 'notification-emails',
        element: <Navigate to="/settings/notifications/" />,
      },
      { index: true, element: <SettingsPage /> },
    ],
  },
];

function SettingsEdiRoute() {
  const { isVisible, isLoading } = useEdiSettingsAccess();

  if (isLoading) {
    return <SettingsPageLayout loading={true} />;
  }

  if (!isVisible) {
    return <Navigate to="/settings/general" replace={true} />;
  }

  return <SettingsEdiPage />;
}

function SettingsSubscriptionRoute() {
  const isSeatBasedPriceEnabled = useFlag('seats_based_pricing');

  return isSeatBasedPriceEnabled ? (
    <SettingsSubscriptionPage />
  ) : (
    <SettingsSubscriptionLegacyPage />
  );
}

function SettingsPage() {
  const isMobile = useResponsiveValue(true, true, false);

  if (isMobile) {
    return <SidebarContainer sidebar={<SettingsSidebar />} />;
  }

  return <Navigate to="/settings/general" />;
}
